import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface CriteriaCondition {
  field: string;
  operator: string;
  value: string;
}

interface ConditionGroupProps {
  groupIndex: number;
  logic: "and" | "or";
  conditions: CriteriaCondition[];
  onLogicChange: (logic: "and" | "or") => void;
  onConditionsChange: (conditions: CriteriaCondition[]) => void;
  onRemoveGroup: () => void;
  canRemoveGroup: boolean;
}

const deviceFields = [
  { value: "os_type", label: "OS Type" },
  { value: "os_version", label: "OS Version" },
  { value: "battery_level", label: "Battery Level" },
  { value: "battery_status", label: "Battery Status" },
  { value: "status", label: "Device Status" },
  { value: "ip_address", label: "IP Address" },
  { value: "hostname", label: "Hostname" },
  { value: "serial_number", label: "Serial Number" },
];

const operators = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "greater_than", label: "Greater Than" },
  { value: "greater_than_or_equal", label: "Greater Than or Equal" },
  { value: "less_than", label: "Less Than" },
  { value: "less_than_or_equal", label: "Less Than or Equal" },
];

export function ConditionGroup({
  groupIndex,
  logic,
  conditions,
  onLogicChange,
  onConditionsChange,
  onRemoveGroup,
  canRemoveGroup,
}: ConditionGroupProps) {
  const addCondition = () => {
    const newConditions = [
      ...conditions,
      { field: "", operator: "", value: "" },
    ];
    onConditionsChange(newConditions);
  };

  const removeCondition = (index: number) => {
    if (conditions.length === 1) return;
    const newConditions = conditions.filter((_, i) => i !== index);
    onConditionsChange(newConditions);
  };

  const updateCondition = (index: number, field: keyof CriteriaCondition, value: string) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    onConditionsChange(newConditions);
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            Group {groupIndex + 1}
          </span>
          <Select value={logic} onValueChange={onLogicChange}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">ALL</SelectItem>
              <SelectItem value="or">ANY</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            of these conditions:
          </span>
        </div>
        {canRemoveGroup && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemoveGroup}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {conditions.map((condition, index) => (
          <div key={index} className="space-y-2">
            {index > 0 && (
              <div className="text-center">
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded">
                  {logic.toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 p-2 border rounded bg-background/50">
              <Select
                value={condition.field}
                onValueChange={(value) => updateCondition(index, "field", value)}
              >
                <SelectTrigger className="flex-1 bg-background">
                  <SelectValue placeholder="Choose property" />
                </SelectTrigger>
                <SelectContent>
                  {deviceFields.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={condition.operator}
                onValueChange={(value) => updateCondition(index, "operator", value)}
              >
                <SelectTrigger className="w-32 bg-background">
                  <SelectValue placeholder="is" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator) => (
                    <SelectItem key={operator.value} value={operator.value}>
                      {operator.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                className="flex-1 bg-background"
                placeholder="value"
                value={condition.value}
                onChange={(e) => updateCondition(index, "value", e.target.value)}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeCondition(index)}
                disabled={conditions.length === 1}
                className="shrink-0 h-8 w-8 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addCondition}
        className="w-full mt-3 h-8"
        size="sm"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Condition
      </Button>
    </div>
  );
}